import { useEffect, useState } from 'react';
import type { CanonicalDocument, PointMm } from '@besbello/design-core';
import { MockupPrenda, type EncajeMockup } from './MockupPrenda.js';
import { api } from '../api/client.js';

/**
 * EL «3D», HONESTO: un giro entre las CARAS REALES de la prenda.
 *
 * El 3D de antes era un cilindro del color de la prenda con el diseño pegado
 * plano por delante: no sabía de qué prenda era, no enseñaba la espalda, no
 * tenía en cuenta la talla ni los pliegues, y el estampado resbalaba sobre la
 * malla. En pantalla, «el 3D no se ve».
 *
 * Esto lo sustituye por lo que de verdad hay: la FOTO real de cada cara —con su
 * color, sus pliegues y su estampado deformado— y un volteo 3D entre ellas
 * (frente ⇄ espalda). No finge geometría: gira fotos reales. Es barato (sin
 * WebGL, funciona donde `three` fallaba) y muestra por fin la espalda, el color
 * real y el tamaño según la talla.
 *
 * Los costados solo existen en el 7 % del catálogo, así que el eje es
 * frente ⇄ espalda; si el grupo tiene más caras, se elige cuál va detrás.
 */

export interface CaraPrenda {
  clave: string;
  titulo: string;
  document: CanonicalDocument;
  zonePolygonMm: PointMm[];
  zoneOffsetMm: { x: number; y: number };
  encaje: EncajeMockup;
  assetUrls: Record<string, string>;
  dark: boolean;
  caraPedida: string;
  caraMostrada: string | null;
  techniqueId?: string;
}

type CatalogoCaras = {
  views: Array<{
    code: string;
    mmPerPixel: number;
    anchor: { xFrac: number; yFrac: number } | null;
    mockupProvenance: 'inherited' | 'adjusted' | 'exception';
    mockupConfidence: number;
  }>;
  colores: Array<{ id: string; imagesByView: Record<string, string | null>; dark: boolean }>;
  zones: Array<{
    zoneId: string;
    viewCode: string;
    name: string;
    polygonMm: Array<{ x: number; y: number }>;
    anchorOffsetMm?: { x: number; y: number };
  }>;
};

export function CarruselPrendas({
  caraActual,
  otras,
  colorId,
  catalogo,
  factorTalla,
  alto,
}: {
  caraActual: CaraPrenda;
  otras: Array<{ designId: string; zoneId: string; viewCode: string; techniqueId: string; layers: number }>;
  colorId: string;
  catalogo: CatalogoCaras;
  factorTalla: number;
  alto?: string;
}) {
  const conContenido = otras.filter((z) => z.layers > 0);
  const clave = conContenido.map((z) => z.designId).join(',');
  // SOLO lo que no depende del color: el documento y sus assets. La foto, el
  // color oscuro y la cara mostrada se resuelven en RENDER (abajo), porque
  // dependen del color elegido y cambian sin que cambien los diseños. Guardar
  // aquí la foto ya resuelta dejaba la cara trasera con el color anterior al
  // cambiar de color —el mismo fallo que ya evita `OtrasCaras`—.
  const [docs, setDocs] = useState<
    Record<string, { doc: CanonicalDocument; assetUrls: Record<string, string> }>
  >({});

  // Traer el documento y los assets de las otras caras del grupo. Una que no
  // cargue no impide voltear a las demás.
  useEffect(() => {
    if (conContenido.length === 0) {
      setDocs({});
      return;
    }
    let vivo = true;
    void (async () => {
      const entradas: Record<string, { doc: CanonicalDocument; assetUrls: Record<string, string> }> = {};
      for (const z of conContenido) {
        try {
          const d = await api.getDesign(z.designId);
          const assets = (await fetch(`/api/designs/${z.designId}/assets`, { credentials: 'include' }).then((r) =>
            r.json(),
          )) as { items?: Array<{ checksum: string; previewUrl: string }> };
          const map: Record<string, string> = {};
          for (const a of assets.items ?? []) map[a.checksum] = a.previewUrl;
          entradas[z.designId] = { doc: d.document as CanonicalDocument, assetUrls: map };
        } catch {
          /* una cara que no carga no rompe el carrusel */
        }
      }
      if (vivo) setDocs(entradas);
    })();
    return () => {
      vivo = false;
    };
    // `clave` resume qué caras hay que traer.
  }, [clave]);

  // Se arma cada cara en RENDER, con el color ACTUAL: así el volteo recolorea
  // al cambiar de color, no solo al cambiar de diseño.
  const resueltas: CaraPrenda[] = conContenido.flatMap((z) => {
    const d = docs[z.designId];
    const color = catalogo.colores.find((c) => c.id === colorId);
    const imageUrl = color?.imagesByView?.[z.viewCode] ?? color?.imagesByView?.front ?? null;
    const vista = catalogo.views.find((v) => v.code === z.viewCode);
    const zona = catalogo.zones.find((zz) => zz.zoneId === z.zoneId && zz.viewCode === z.viewCode);
    if (!d || !imageUrl || !vista || !zona) return [];
    return [
      {
        clave: z.designId,
        titulo: zona.name,
        document: d.doc,
        zonePolygonMm: zona.polygonMm,
        zoneOffsetMm: zona.anchorOffsetMm ?? { x: 0, y: 0 },
        encaje: {
          imageUrl,
          mmPerPixel: vista.mmPerPixel,
          anchor: vista.anchor,
          provenance: vista.mockupProvenance,
          confidence: vista.mockupConfidence,
        },
        assetUrls: d.assetUrls,
        dark: color?.dark ?? false,
        caraPedida: z.viewCode,
        caraMostrada: color?.imagesByView?.[z.viewCode] ? z.viewCode : 'front',
        techniqueId: z.techniqueId,
      },
    ];
  });

  const caras = [caraActual, ...resueltas];
  const [volteada, setVolteada] = useState(false);
  const [traseraIdx, setTraseraIdx] = useState(1);

  // Si cambian las caras, no dejar el índice de la trasera fuera de rango.
  const trasera = caras[Math.min(traseraIdx, caras.length - 1)] ?? caras[0]!;
  const frontal = caras[0]!;

  const pintar = (cara: CaraPrenda) => (
    <MockupPrenda
      document={cara.document}
      zonePolygonMm={cara.zonePolygonMm}
      zoneOffsetMm={cara.zoneOffsetMm}
      encaje={cara.encaje}
      assetUrls={cara.assetUrls}
      dark={cara.dark}
      caraPedida={cara.caraPedida}
      caraMostrada={cara.caraMostrada}
      factorTalla={factorTalla}
      techniqueId={cara.techniqueId}
    />
  );

  const hayVuelta = caras.length > 1;

  return (
    <div className="space-y-3">
      <div className={`relative mx-auto max-w-md ${alto ?? ''}`} style={{ perspective: '1400px' }}>
        <div
          className="relative h-full w-full"
          data-test="carrusel-prendas"
          style={{
            transformStyle: 'preserve-3d',
            transform: `rotateY(${volteada ? 180 : 0}deg)`,
            transition: 'transform 0.7s cubic-bezier(0.4, 0.1, 0.2, 1)',
          }}
        >
          {/* Cara de delante del volteo: la del diseño que se está trabajando. */}
          <div className="absolute inset-0" style={{ backfaceVisibility: 'hidden', WebkitBackfaceVisibility: 'hidden' }}>
            {pintar(frontal)}
          </div>
          {/* Cara de detrás: pre-girada 180° para que al voltear se lea bien,
              no en espejo. */}
          <div
            className="absolute inset-0"
            style={{
              backfaceVisibility: 'hidden',
              WebkitBackfaceVisibility: 'hidden',
              transform: 'rotateY(180deg)',
            }}
          >
            {pintar(trasera)}
          </div>
        </div>
      </div>

      {hayVuelta ? (
        <div className="flex flex-col items-center gap-2">
          <button
            type="button"
            data-test="voltear-prenda"
            onClick={() => setVolteada((v) => !v)}
            className="toque rounded-lg border border-borde px-4 py-2 text-sm font-medium"
          >
            {volteada ? `Ver ${frontal.titulo.toLowerCase()}` : `Ver ${trasera.titulo.toLowerCase()}`}
          </button>
          {/* Si el grupo tiene más de dos caras, elegir cuál va detrás. */}
          {caras.length > 2 && (
            <ul className="flex flex-wrap justify-center gap-1.5">
              {caras.slice(1).map((cara, i) => {
                const activa = traseraIdx === i + 1;
                return (
                  <li key={cara.clave}>
                    <button
                      type="button"
                      data-test="cara-carrusel"
                      aria-pressed={activa}
                      onClick={() => {
                        setTraseraIdx(i + 1);
                        setVolteada(true);
                      }}
                      className={`toque rounded-md border px-2.5 py-1 text-xs ${
                        activa ? 'border-marca bg-marca text-white' : 'border-borde text-tinta-suave'
                      }`}
                    >
                      {cara.titulo}
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      ) : null}

      <p className="text-center text-xs text-tinta-suave">
        Vista orientativa: son las fotos reales de la prenda con tu diseño. El color y la posición exactos los define el
        arte final; no sustituye a una prueba impresa.
      </p>
    </div>
  );
}
